import { isRoundedPlatform, roundedFullClassname } from '@/helpers/environmentHelper';
import useLanguage, { UseLanguageReturn } from '@/utils/hooks/useLanguage';
import cn from 'classnames';
import _, { dropRight } from 'lodash';
import { useMemo } from 'react';
import { MdRefresh } from 'react-icons/md';
import {
    Corner,
    getNodeAtPath,
    getOtherDirection,
    getPathToCorner,
    MosaicDirection,
    MosaicNode,
    MosaicParent,
    updateTree
} from 'react-mosaic-component';
import { useAppDispatch } from '../../state/hooks';
import { setXplorSpotDesktopLayoutNode, setXplorSpotLaptopLayoutNode } from '../../state/reducers/xplorSpotLayoutSlice';
import useScreenSizes from '../../utils/hooks/useScreenSizes';
import {
    DEFAULT_LAPTOP_LAYOUT,
    DEFAULT_LAYOUT,
    tabIcon,
    tabList,
    default as useLayout,
    default as useXplorSpotLayout,
    XplorSpotModules
} from '../../utils/hooks/useXplorSpotLayout';
import MobileTabNavigation from './MobileTabNavigation';
import TabletTabNavigation from './TabletTabNavigation';

const defaultPropertyValues = tabList.filter((tab) => tab !== 'Notifications');
export const footerClassname = cn(
    'flex bg-brand-background text-brand-module-title text-xs h-16 p-0 sm:h-6 sm:p-0.5',
    { 'mx-3': isRoundedPlatform },
    roundedFullClassname
);

function getAllPropertyValues(obj): Array<string | number> {
    const values: any = [];

    function recursiveGetValues(obj) {
        for (const key in obj) {
            if (typeof obj[key] === 'object' && obj[key] !== null) {
                recursiveGetValues(obj[key]);
            } else {
                values.push(obj[key]);
            }
        }
    }

    recursiveGetValues(obj);
    return values;
}

export default function Footer() {
    const screen = useScreenSizes();
    const { layout, default_layout } = useLayout(screen);

    return (
        <footer className={footerClassname}>
            {(screen.laptop || screen.desktop) && <TabNavigation screen={screen} layout={layout || default_layout} />}
            {screen.tablet && <TabletTabNavigation />}
            {screen.mobile && <MobileTabNavigation />}
        </footer>
    );
}

const TabNavigation = ({ screen, layout }: any) => {
    const dispatch = useAppDispatch();

    const lang = useLanguage();

    const removedModules = useMemo(() => {
        let propertyValues;
        if (typeof layout === 'string') propertyValues = [layout];
        else
            propertyValues = getAllPropertyValues(layout).filter(
                (el) => typeof el !== 'number' && !['row', 'column'].includes(el)
            );
        const difference = _.difference(defaultPropertyValues, propertyValues);
        // difference.sort();
        return difference;
    }, [layout]) as XplorSpotModules[];

    const xplorSpotLayoutConfig = useXplorSpotLayout(screen).config;

    return (
        <div className="flex items-center justify-between w-full">
            <div className="flex gap-0.5">
                <div
                    className={cn(
                        'flex uppercase cursor-pointer bg-brand-neutral-800 hover:bg-brand-neutral-700 text-brand-text font-semibold px-2 gap-2 items-center',
                        roundedFullClassname
                    )}
                    onClick={() => {
                        if (screen.laptop) dispatch(setXplorSpotLaptopLayoutNode(DEFAULT_LAPTOP_LAYOUT));
                        if (screen.desktop) dispatch(setXplorSpotDesktopLayoutNode(DEFAULT_LAYOUT));
                    }}>
                    <MdRefresh className="w-3.5 h-3.5" />
                    {lang.common.resetLayout}
                </div>
                {removedModules.map((mod) => {
                    const Icon = tabIcon[mod];
                    const { title } = xplorSpotLayoutConfig[mod];
                    const langTitle = getLangTitle(mod, lang);

                    return (
                        <div
                            key={mod}
                            className={cn(
                                'flex uppercase cursor-pointer bg-brand-primary hover:bg-brand-primary-light font-semibold px-2 gap-2 items-center text-brand-text',
                                roundedFullClassname
                            )}
                            onClick={() =>
                                addToTopRight(layout, mod, (currentNode) => {
                                    if (screen.laptop) dispatch(setXplorSpotLaptopLayoutNode(currentNode));
                                    if (screen.desktop) dispatch(setXplorSpotDesktopLayoutNode(currentNode));
                                })
                            }>
                            <Icon className="w-3.5 h-3.5" />
                            {langTitle}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const getLangTitle = (mod: XplorSpotModules, lang: UseLanguageReturn) => {
    switch (mod) {
        case 'Order':
            return lang.modules.order;
        case 'Balances':
            return lang.modules.balances;
        case 'Chart':
            return lang.modules.chart;
        case 'Order Book':
            return lang.modules['order-book'];
        case 'Blotter':
            return lang.modules.blotter;
        case 'Markets':
            return lang.modules.markets;
        default:
            return '';
    }
};

const addToTopRight = (layoutNode, moduleToDrop, callback) => {
    let currentNode = layoutNode;
    if (currentNode) {
        const path = getPathToCorner(currentNode, Corner.TOP_RIGHT);
        const parent = getNodeAtPath(currentNode, dropRight(path)) as MosaicParent<XplorSpotModules>;
        const destination = getNodeAtPath(currentNode, path) as MosaicNode<XplorSpotModules>;
        const direction: MosaicDirection = parent ? getOtherDirection(parent.direction) : 'row';

        let first: MosaicNode<XplorSpotModules>;
        let second: MosaicNode<XplorSpotModules>;
        if (direction === 'row') {
            first = destination;
            second = moduleToDrop;
        } else {
            first = moduleToDrop;
            second = destination;
        }

        currentNode = updateTree(currentNode, [
            {
                path,
                spec: {
                    $set: {
                        direction,
                        first,
                        second
                    }
                }
            }
        ]);
    } else {
        currentNode = moduleToDrop;
    }

    callback(currentNode);
};
